// ============================================================================
// Indian mobile number handling — shared by signup / login / forgot-password.
//
// The backend takes a RAW 10-digit number and rejects anything else outright
// (`SendOtpOnWhatsAppbyMobileNumber` returns {status:'Invalid Mobile Number'}
// when the length is not exactly 10). So "07790003000" and "+917790003000" are
// both refused even though the user meant a perfectly good number.
//
// Rather than reject what the user typed, sanitizeMobileInput() normalises the
// common ways people write a number and caps it at 10 digits.
// ============================================================================

/**
 * Valid Indian mobile: exactly 10 digits that do not begin 1-5.
 * (A leading 0 is trunk-dialling and is stripped by sanitizeMobileInput before
 * this is ever applied, so it never reaches the test.)
 */
export const MOBILE_REGEX = /^[^1-5]\d{9}$/;

/**
 * Normalise anything a user might type into a bare 10-digit number.
 *
 *   "+91 77900 03000"  -> "7790003000"
 *   "917790003000"     -> "7790003000"
 *   "07790003000"      -> "7790003000"
 *   "77900030001234"   -> "7790003000"   (capped)
 *
 * Safe to call on every keystroke — it never throws and never lengthens input.
 */
export function sanitizeMobileInput(raw) {
  let digits = String(raw ?? "").replace(/\D/g, "");

  // Country code, with or without the "+" (already stripped above).
  // Only strip it when something remains that could be a real number, so
  // someone typing a number that legitimately begins "91…" is not mangled.
  if (digits.length > 10 && digits.startsWith("91")) {
    digits = digits.slice(2);
  }

  // Trunk-dialling zero(s): "0" then the number.
  digits = digits.replace(/^0+/, "");

  return digits.slice(0, 10);
}

/** True when the value is a complete, valid Indian mobile number. */
export function isValidMobile(value) {
  return MOBILE_REGEX.test(String(value ?? ""));
}

/**
 * Human-readable reason a number is not acceptable, or "" when it is fine.
 * Kept separate from isValidMobile so callers can show something specific.
 */
export function mobileValidationError(value) {
  const v = String(value ?? "").trim();

  if (!v) return "Please enter your mobile number.";
  if (!/^\d+$/.test(v)) return "Mobile number must contain digits only.";
  if (v.length < 10) return `Mobile number must be 10 digits — you entered ${v.length}.`;
  if (v.length > 10) return `Mobile number must be 10 digits — you entered ${v.length}.`;
  if (/^[1-5]/.test(v)) return "A mobile number cannot start with 1, 2, 3, 4 or 5.";

  return "";
}

/**
 * The WhatsApp account the OTP is delivered from. Shown to the user as a
 * "open WhatsApp" shortcut so they can jump straight to the chat and read the
 * code instead of hunting for it.
 */
export const OTP_SENDER_WHATSAPP = "918624827530";

/** Opens the OTP sender's WhatsApp chat in a new tab. */
export function openOtpWhatsApp() {
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const url = isMobileDevice
    ? `https://wa.me/${OTP_SENDER_WHATSAPP}`
    : `https://web.whatsapp.com/send?phone=${OTP_SENDER_WHATSAPP}`;
  window.open(url, "_blank", "noopener,noreferrer");
}
